import { useEffect, useState } from "react";
import axios from "axios";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faArrowTrendDown, faEye, faBullseye, faLightbulb } from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

const CHART_TOOLTIP = {
  contentStyle: { background: "#0E1E38", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: 12, color: "#F2EBD9", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" },
  labelStyle: { color: "rgba(242,235,217,0.4)" },
};

const CARD: React.CSSProperties = {
  background: "#0E1E38",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
};

type SuggestionType = "raise" | "lower" | "watch" | "promo";

interface Suggestion {
  type: SuggestionType;
  urgency: "high" | "medium" | "low";
  title: string;
  message: string;
  occupancy: number;
  dateRange: string;
  suggestedAction: string;
}

interface ForecastDay {
  date: string;
  label: string;
  occupancy: number;
  booked: number;
  total: number;
}

interface PricingData {
  suggestions: Suggestion[];
  occupancyForecast: ForecastDay[];
  avgOccupancy14d: number;
  peakDay: { date: string; occupancy: number };
  lowestDay: { date: string; occupancy: number };
}

const TYPE_CONFIG: Record<SuggestionType, { color: string; bg: string; border: string; icon: any; label: string }> = {
  raise: { color: "#059669", bg: "rgba(16,185,129,0.07)", border: "rgba(16,185,129,0.2)", icon: faChartLine, label: "Raise Rates" },
  lower: { color: "#ef4444", bg: "rgba(239,68,68,0.07)", border: "rgba(239,68,68,0.2)", icon: faArrowTrendDown, label: "Lower Rates" },
  watch: { color: "#d97706", bg: "rgba(245,158,11,0.07)", border: "rgba(245,158,11,0.2)", icon: faEye, label: "Monitor" },
  promo: { color: "#7c3aed", bg: "rgba(124,58,237,0.07)", border: "rgba(124,58,237,0.2)", icon: faBullseye, label: "Run Promo" },
};

const URGENCY_COLOR = { high: "#ef4444", medium: "#d97706", low: "#64748b" };

function OccupancyBar({ value }: { value: number }) {
  const color = value >= 80 ? "#059669" : value >= 60 ? "#d97706" : value >= 30 ? "#6366f1" : "#ef4444";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: "rgba(201,168,76,0.12)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(100, value)}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.4s" }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 36, textAlign: "right" }}>{value}%</span>
    </div>
  );
}

export default function PricingSuggestionsPage() {
  const [data, setData] = useState<PricingData | null>(null);
  const [loading, setLoading] = useState(true);

  const authHeaders = {
    Authorization: `Bearer ${localStorage.getItem("ai2_token")}`,
    "x-hotel": localStorage.getItem("ai2_hotel") || "",
  };

  useEffect(() => {
    axios.get(`${API}/api/dashboard/pricing-suggestions`, { headers: authHeaders })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const chartData = data?.occupancyForecast.map(d => ({
    name: d.label,
    occupancy: d.occupancy,
    booked: d.booked,
    total: d.total,
  })) || [];

  return (
    <DashboardLayout>
      <div style={{ padding: "28px 32px", minHeight: "100vh" }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: "#FDFAF4", margin: 0, letterSpacing: "-0.5px" }}>Dynamic Pricing</h1>
          <p style={{ fontSize: 13, color: "rgba(242,235,217,0.45)", margin: "4px 0 0" }}>
            AI-driven pricing recommendations based on upcoming occupancy trends
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", color: "rgba(242,235,217,0.45)", padding: 80 }}>Analyzing occupancy data…</div>
        ) : !data ? (
          <div style={{ textAlign: "center", color: "#f87171", padding: 80 }}>Failed to load data</div>
        ) : (
          <>
            {/* KPI strip */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
              {[
                { label: "Avg Occupancy (14d)", value: `${data.avgOccupancy14d}%`, color: data.avgOccupancy14d >= 70 ? "#059669" : data.avgOccupancy14d >= 40 ? "#d97706" : "#ef4444" },
                { label: "Peak Day", value: data.peakDay ? `${data.peakDay.occupancy}%` : "—", color: "#059669", sub: data.peakDay?.date },
                { label: "Lowest Day", value: data.lowestDay ? `${data.lowestDay.occupancy}%` : "—", color: "#ef4444", sub: data.lowestDay?.date },
                { label: "Suggestions", value: data.suggestions.length, color: "#6366f1", sub: `${data.suggestions.filter(s => s.urgency === "high").length} high priority` },
              ].map(k => (
                <div key={k.label} style={{ ...CARD, padding: "18px 20px" }}>
                  <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px" }}>{k.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: k.color, margin: "6px 0 2px", letterSpacing: "-0.5px" }}>{k.value}</div>
                  {k.sub && <div style={{ fontSize: 11, color: "rgba(242,235,217,0.3)" }}>{k.sub}</div>}
                </div>
              ))}
            </div>

            {/* Main layout */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 20 }}>
              {/* Left: Chart + forecast grid */}
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* Occupancy chart */}
                <div style={{ ...CARD, padding: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#FDFAF4", marginBottom: 16 }}>14-Day Occupancy Forecast</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={chartData} margin={{ left: -10 }}>
                      <defs>
                        <linearGradient id="occGradP" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                      <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={v => `${v}%`} domain={[0, 100]} />
                      <Tooltip {...CHART_TOOLTIP} formatter={(v: number) => [`${v}%`, "Occupancy"]} />
                      <ReferenceLine y={80} stroke="rgba(5,150,105,0.5)" strokeDasharray="4 4" label={{ value: "80% — raise rates", fill: "#059669", fontSize: 10 }} />
                      <ReferenceLine y={30} stroke="rgba(239,68,68,0.5)" strokeDasharray="4 4" label={{ value: "30% — consider promo", fill: "#ef4444", fontSize: 10 }} />
                      <Area type="monotone" dataKey="occupancy" stroke="#6366f1" strokeWidth={2} fill="url(#occGradP)" dot={{ fill: "#6366f1", r: 3 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Day-by-day grid */}
                <div style={{ ...CARD, overflow: "hidden" }}>
                  <div style={{ padding: "14px 18px", borderBottom: "1px solid #e2e8f0" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#FDFAF4" }}>Day-by-Day Forecast</span>
                  </div>
                  <div style={{ padding: "4px 0" }}>
                    {data.occupancyForecast.map((d, i) => {
                      const isHigh = d.occupancy >= 80;
                      const isLow = d.occupancy < 30;
                      return (
                        <div key={i} style={{
                          display: "grid", gridTemplateColumns: "130px 1fr 80px 80px",
                          alignItems: "center", gap: 12,
                          padding: "10px 18px",
                          borderBottom: "1px solid #f1f5f9",
                          background: isHigh ? "rgba(5,150,105,0.03)" : isLow ? "rgba(239,68,68,0.03)" : "transparent",
                        }}>
                          <span style={{ fontSize: 13, color: "rgba(242,235,217,0.45)" }}>{d.label}</span>
                          <OccupancyBar value={d.occupancy} />
                          <span style={{ fontSize: 12, color: "rgba(242,235,217,0.3)", textAlign: "right" }}>{d.booked}/{d.total} rooms</span>
                          <span style={{ fontSize: 11, fontWeight: 700, textAlign: "right",
                            color: isHigh ? "#059669" : isLow ? "#ef4444" : "#cbd5e1",
                          }}>
                            {isHigh ? "↑ RAISE" : isLow ? "↓ PROMO" : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right: Suggestions */}
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#FDFAF4" }}>Recommendations</div>
                {data.suggestions.length === 0 ? (
                  <div style={{ ...CARD, padding: 24, textAlign: "center", color: "rgba(242,235,217,0.3)", fontSize: 13 }}>
                    No urgent pricing actions needed right now.
                  </div>
                ) : (
                  data.suggestions.map((s, i) => {
                    const cfg = TYPE_CONFIG[s.type];
                    return (
                      <div key={i} style={{
                        background: cfg.bg, border: `1px solid ${cfg.border}`,
                        borderRadius: 14, padding: 16,
                      }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <FontAwesomeIcon icon={cfg.icon} style={{ fontSize: 16, color: cfg.color }} />
                            <span style={{ fontSize: 14, fontWeight: 700, color: cfg.color }}>{s.title}</span>
                          </div>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20, textTransform: "uppercase",
                            background: `${URGENCY_COLOR[s.urgency]}18`, color: URGENCY_COLOR[s.urgency],
                            whiteSpace: "nowrap",
                          }}>
                            {s.urgency}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: "rgba(242,235,217,0.45)", marginBottom: 8, lineHeight: 1.5 }}>{s.message}</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)" }}>{s.dateRange}</div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: cfg.color, textAlign: "right" }}>
                            {s.occupancy}% booked
                          </div>
                        </div>
                        <div style={{
                          marginTop: 10, padding: "8px 12px",
                          background: "rgba(0,0,0,0.04)", borderRadius: 8,
                          fontSize: 12, color: "#F2EBD9", fontWeight: 600,
                        }}>
                          <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: 6 }} /> {s.suggestedAction}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
